/**
 * 统一的轮询服务
 * 用于简化任务状态轮询逻辑，避免重复代码和竞态条件
 * 
 * @created 2025-11-05
 */

export interface PollingOptions {
  /** 轮询间隔（毫秒），默认 2000ms */
  interval?: number;
  /** 最大轮询次数，默认 150 次（5分钟） */
  maxAttempts?: number;
  /** 错误回调 */
  onError?: (error: Error, attempt: number) => void;
  /** 每次轮询前的回调，返回 false 可提前停止 */
  onBeforePoll?: (attempt: number) => boolean | void;
}

export type PollingStopCallback = () => void;

/**
 * 统一的轮询服务类
 */
export class PollingService {
  private intervals: Map<string, NodeJS.Timeout> = new Map();
  private abortControllers: Map<string, AbortController> = new Map();

  /**
   * 开始轮询
   * 
   * @param key - 唯一标识符，用于停止轮询
   * @param fetchFn - 获取数据的函数
   * @param onSuccess - 成功回调，返回 true 表示停止轮询
   * @param options - 轮询选项
   * @returns 停止轮询的函数
   */
  poll<T>(
    key: string,
    fetchFn: (signal?: AbortSignal) => Promise<T>,
    onSuccess: (data: T, attempt: number) => boolean | void,
    options: PollingOptions = {}
  ): PollingStopCallback {
    const {
      interval = 2000,
      maxAttempts = 150, // 默认 5 分钟（150 * 2秒）
      onError,
      onBeforePoll
    } = options;

    // 如果已有相同的轮询在运行，先停止它
    this.stop(key);

    let attempts = 0;
    const abortController = new AbortController();
    this.abortControllers.set(key, abortController);

    const poll = async () => {
      // 检查是否已中止
      if (abortController.signal.aborted) {
        return;
      }

      // 检查是否超过最大次数
      if (attempts >= maxAttempts) {
        this.stop(key);
        onError?.(new Error('轮询超时'), attempts);
        return;
      }

      // 执行前置回调
      if (onBeforePoll) {
        const shouldContinue = onBeforePoll(attempts);
        if (shouldContinue === false) {
          this.stop(key);
          return;
        }
      }

      attempts++;

      try {
        const data = await fetchFn(abortController.signal);
        
        // 检查是否已中止（在 fetch 期间可能被中止）
        if (abortController.signal.aborted) {
          return;
        }

        const shouldStop = onSuccess(data, attempts);
        
        if (shouldStop === true) {
          this.stop(key);
        }
      } catch (error) {
        // 如果是中止错误，不触发 onError
        if (error instanceof Error && error.name === 'AbortError') {
          return;
        }

        // 其他错误继续轮询，但触发 onError 回调
        onError?.(error as Error, attempts);
        // 注意：错误不停止轮询，允许网络临时故障恢复
      }
    };

    // 立即执行一次
    poll();

    // 设置定时轮询
    const intervalId = setInterval(poll, interval);
    this.intervals.set(key, intervalId);

    // 返回停止函数
    return () => this.stop(key);
  }

  /**
   * 停止指定的轮询
   */
  stop(key: string): void {
    // 清除定时器
    const intervalId = this.intervals.get(key);
    if (intervalId) {
      clearInterval(intervalId);
      this.intervals.delete(key);
    }

    // 中止请求
    const abortController = this.abortControllers.get(key);
    if (abortController) {
      abortController.abort();
      this.abortControllers.delete(key);
    }
  }

  /**
   * 停止所有轮询
   */
  stopAll(): void {
    // 停止所有定时器
    this.intervals.forEach((id) => clearInterval(id));
    this.intervals.clear();

    // 中止所有请求
    this.abortControllers.forEach((controller) => controller.abort());
    this.abortControllers.clear();
  }

  /**
   * 检查指定 key 的轮询是否正在运行
   */
  isPolling(key: string): boolean {
    return this.intervals.has(key);
  }
}

// 导出单例实例
export const pollingService = new PollingService();

/**
 * 指数退避重试函数
 * 
 * @param fn - 要重试的函数
 * @param options - 重试选项
 */
export async function retryWithBackoff<T>(
  fn: () => Promise<T>,
  options: {
    maxRetries?: number;
    initialDelay?: number;
    maxDelay?: number;
    backoffFactor?: number;
  } = {}
): Promise<T> {
  const {
    maxRetries = 3,
    initialDelay = 1000,
    maxDelay = 10000,
    backoffFactor = 2
  } = options;

  let delay = initialDelay;

  for (let attempt = 0; attempt <= maxRetries; attempt++) {
    try {
      return await fn();
    } catch (error) {
      if (attempt === maxRetries) {
        throw error;
      }

      await new Promise((resolve) => setTimeout(resolve, delay));
      delay = Math.min(delay * backoffFactor, maxDelay);
    }
  }

  throw new Error('Retry exhausted');
}

